import React, { useEffect } from "react";
import { PowerBIEmbed } from "powerbi-client-react";
import PropTypes from "prop-types";

import usePowerBIEmbed from "../../hooks/usePowerBIEmbed";
import usePowerBIButtonNavigation from "../../hooks/usePowerBIButtonNavigation";
import { getLogger } from "@hoteltradergit/ht-telemetry-ui-lib";
import { PowerBINavigationProvider } from "../../contexts/PowerBINavigationContext";
import HTLoader from "../common/ht-loader/HTLoader";

import "./report.scss";

// CSS Class to be passed to the embedded component
const REPORT_CLASS = "report-container";

const AnalyticsHubReportContent = ({
  pageName = null,
  onPagesLoaded = null,
}) => {
  const {
    report,
    reportConfig,
    isLoaded,
    eventHandlersMap,
    handleEmbedComponent,
  } = usePowerBIEmbed("Analytics Hub", [], "Supplier Portal Report");

  const buttonHandlers = usePowerBIButtonNavigation(report);

  // Merge button handlers into event handlers map
  React.useEffect(() => {
    if (buttonHandlers?.length > 0 && eventHandlersMap) {
      buttonHandlers.forEach(([eventName, handler]) => {
        eventHandlersMap.set(eventName, handler);
      });
    }
  }, [buttonHandlers, eventHandlersMap]);

  // Navigate to specific page when pageName changes
  useEffect(() => {
    const navigateToPage = async () => {
      if (!report || !pageName) return;

      try {
        const pages = await report.getPages();

        // Send pages to parent component for navigation menu
        if (onPagesLoaded && pages) {
          const visiblePages = pages.filter(
            (page) => !page.visibility || page.visibility === 0
          );
          onPagesLoaded(visiblePages);
        }

        // Find the page by displayName (case-insensitive)
        const targetPage = pages.find(
          (page) => page.displayName.toLowerCase() === pageName.toLowerCase()
        );

        if (targetPage && !targetPage.isActive) {
          await targetPage.setActive();
        } else if (!targetPage) {
          getLogger().warn("AnalyticsHubReport", {
            type: "Warn",
            page_url: window.location.href,
            error_desc: "Page not found: " + pageName,
          });
        }
      } catch (error) {
        getLogger().error("AnalyticsHubReport", {
          type: "Error",
          page_url: window.location.href,
          error_desc: "Error navigating to page",
          error_stack: error?.stack ?? null,
        });
      }
    };

    if (isLoaded) {
      navigateToPage();
    }
  }, [pageName, report, isLoaded, onPagesLoaded]);

  return (
    <>
      {!isLoaded && <HTLoader />}
      <PowerBIEmbed
        embedConfig={reportConfig}
        cssClassName={REPORT_CLASS + (isLoaded ? "" : " invisible")}
        getEmbeddedComponent={handleEmbedComponent}
        eventHandlers={eventHandlersMap}
      />
    </>
  );
};

AnalyticsHubReportContent.propTypes = {
  pageName: PropTypes.string,
  onPagesLoaded: PropTypes.func,
};

// Wrap with PowerBINavigationProvider to enable navigation context
const AnalyticsHubReport = (props) => {
  return (
    <PowerBINavigationProvider>
      <AnalyticsHubReportContent {...props} />
    </PowerBINavigationProvider>
  );
};

export default AnalyticsHubReport;
